import { isAxiosError } from "axios";

import api from "../api";

import {
  CreateDynamicFormTransactionRequest,
  CreateDynamicFormTransactionResponse,
  DynamicFormField,
  DynamicFormLookupOption,
  DynamicFormLookupType,
  DynamicFormResponse,
} from "./types";

/** Creates a dynamic form transaction in Certinal. Returns the new transaction ID. */
export const createDynamicFormTransaction = async (
  request: CreateDynamicFormTransactionRequest
): Promise<string> => {
  const url = `${process.env.REACT_APP_ECONSENT_SUFFIX}/dynamic-form`;
  const res = await api.post<CreateDynamicFormTransactionResponse>(url, request);
  return res.data.transactionId;
};

export const getDynamicForm = async (params: {
  hn: string;
  formId: string;
  sessionIdBase64: string;
  language?: string;
}): Promise<DynamicFormResponse> => {
  const { hn, formId, sessionIdBase64, language } = params;

  const searchParams = new URLSearchParams({ sessionId: sessionIdBase64 });
  if (language) searchParams.set('language', language);
  
  const url = `${process.env.REACT_APP_ECONSENT_SUFFIX}/dynamic-form/hns/${hn}/forms/${formId}?${searchParams.toString()}`;
  const res = await api.get<DynamicFormResponse>(url);
  return res.data;
};

export const getDynamicFormLookupOptions = async (params: {
  lookupType: DynamicFormLookupType | string;
  keyword?: string;
  formId?: string;
  language?: string;
}): Promise<DynamicFormLookupOption[]> => {
  const { lookupType, keyword, formId, language } = params;

  const searchParams = new URLSearchParams({ lookupType: String(lookupType) });
  if (keyword != null && keyword !== '') searchParams.set('keyword', keyword);
  if (formId != null && formId !== '') searchParams.set('formId', formId);
  if (language != null && language !== '') searchParams.set('language', language);

  const url = `${process.env.REACT_APP_ECONSENT_SUFFIX}/dynamic-form/lookup-options?${searchParams.toString()}`;
  const res = await api.get<DynamicFormLookupOption[]>(url);
  return res.data;
};

/**
 * Loads field definition from DynamicFieldConfiguration by template placeholder token
 * (e.g. value inside `{{laterality}}` → `fieldName: "laterality"`).
 * Returns null when the backend has no config for that name (404).
 */
export const getDynamicFormFieldConfigByTemplateFieldName = async (params: {
  fieldName: string;
}): Promise<DynamicFormField | null> => {
  const trimmed = params.fieldName?.trim();
  if (!trimmed) return null;

  const url = `${process.env.REACT_APP_ECONSENT_SUFFIX}/dynamic-form/fields/${encodeURIComponent(trimmed)}/config`;
  try {
    const res = await api.get<DynamicFormField>(url);
    return res.data;
  } catch (e) {
    if (isAxiosError(e) && e.response?.status === 404) return null;
    throw e;
  }
};